import {creates, hoistCmp} from '@xh/hoist/core';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {StockChartWidgetModel} from './StockChartWidgetModel';
import {toolbar} from '@xh/hoist/desktop/cmp/toolbar';
import {button} from '@xh/hoist/desktop/cmp/button';
import {numberInput} from '@xh/hoist/desktop/cmp/input';
import {chart} from '@xh/hoist/cmp/chart';
import {Icon} from '@xh/hoist/icon';
import {tooltip} from '@xh/hoist/kit/blueprint';
import {indicatorPicker} from '../../indicators/IndicatorPicker';
import {indicatorParamsPicker} from '../../indicators/IndicatorParamsPicker';
import {isActiveIndicator} from '../../indicators/TechnicalIndicators';
import {div, hbox, hspacer, input, p, placeholder, span, vbox} from '@xh/hoist/cmp/layout';
import {fmtNumber, fmtPrice} from '@xh/hoist/format';
import {securityPicker} from '../../../../cmp/securityPicker/SecurityPicker';
import {compactToolbarButton, compactToolbarMenuButton, toolbarButtonSeparator} from '../../../../cmp/toolbar/Toolbar';
import {changeChip, signDir} from '../../../../cmp/kpi/Kpi';
import {rangeBar} from '../../../../cmp/miniCharts/MiniCharts';
import {stockToolsToolbar} from './StockToolsToolbar';
import './StockChartWidget.scss';
import './StockToolsToolbar.scss';
import {chartSimpleIcon, sliderIcon} from '../../../../../core/Icons';

function tickClass(tick: 'pos' | 'neg' | null): string {
    return tick === 'pos' ? 'xh-pos-val' : tick === 'neg' ? 'xh-neg-val' : '';
}

export const stockChartWidget = hoistCmp.factory<StockChartWidgetModel>({
    model: creates(() => StockChartWidgetModel),
    render({model}) {
        return panel({
            tbar: tbar(),
            item: model.warning
                ? placeholder({items: [Icon.chartLine(), p(model.warning)]})
                : vbox({
                      position: 'relative',
                      flex: 1,
                      minHeight: 0,
                      items: [chart({flex: 1}), annotationEditPopup()]
                  }),
            mask: model.apiTask
        });
    }
});

const annotationEditPopup = hoistCmp.factory<StockChartWidgetModel>({
    render({model}) {
        if (!model.currentAnnotation) return null;

        return div({
            className: 'stock-tools-annotation-popup',
            items: [
                div({
                    className: 'stock-tools-annotation-popup__header',
                    items: [
                        span('Edit annotation'),
                        compactToolbarButton({
                            icon: Icon.x(),
                            onClick: () => model.closeAnnotationEditor()
                        })
                    ]
                }),
                div({
                    className: 'stock-tools-annotation-popup__row',
                    items: [
                        span('Color'),
                        input({
                            type: 'color',
                            value: model.annotationStrokeColor,
                            onChange: e => model.setAnnotationStrokeColor((e.target as HTMLInputElement).value)
                        })
                    ]
                }),
                div({
                    className: 'stock-tools-annotation-popup__row',
                    items: [
                        span('Width'),
                        numberInput({
                            value: model.annotationStrokeWidth,
                            min: 1,
                            max: 20,
                            width: 70,
                            onCommit: v => model.setAnnotationStrokeWidth(v)
                        })
                    ]
                }),
                button({
                    text: 'Save',
                    intent: 'primary',
                    onClick: () => model.applyAnnotationStyle()
                })
            ]
        });
    }
});

const tbar = hoistCmp.factory<StockChartWidgetModel>({
    render({model}) {
        const chartModel = model,
            settingsModel = model.settingsFormModel,
            formModel = settingsModel.formModel,
            {quote} = model,
            price = quote ? (quote.bid + quote.ask) / 2 : null;

        return toolbar({
            className: model.stockToolsBindingsClassName,
            compact: true,
            items: [
                securityPicker({
                    compact: true,
                    ticker: formModel.values.ticker,
                    disabled: formModel.values.isLinked,
                    onSecuritySelected: sec => formModel.setValues({ticker: sec.ticker}),
                    width: 100,
                    showSearchIcon: true,
                    keepSelection: true
                }),
                compactToolbarButton({
                    omit: !chartModel.canLink,
                    icon: Icon.link(),
                    intent: formModel.values.isLinked ? 'warning' : null,
                    active: formModel.values.isLinked,
                    onClick: () => {
                        formModel.setValues({isLinked: !formModel.values.isLinked});
                    },
                    tooltip: 'Link to main chart'
                }),
                toolbarButtonSeparator(),

                quote?.name
                    ? tooltip({
                          compact: true,
                          content: quote.name,
                          item: span({
                              className: 'stock-chart-widget__quote-name',
                              item: quote.name
                          })
                      })
                    : null,
                toolbarButtonSeparator(!quote),
                hspacer(10),
                quote
                    ? span({
                          className: `stock-chart-widget__quote-price ${tickClass(model.priceTick)}`,
                          item: fmtPrice(price)
                      })
                    : null,
                quote
                    ? changeChip(
                          fmtNumber(quote.priceChange, {precision: 2, withPlusSign: true}),
                          quote.priceChangePct,
                          signDir(quote.priceChange),
                          'stock-chart-widget__quote-change'
                      )
                    : null,
                quote
                    ? vbox({
                          className: 'stock-chart-widget__quote-bidask',
                          omit: !quote.isListed,
                          items: [
                              hbox([
                                  span({className: 'stock-chart-widget__quote-label', item: 'B:'}),
                                  span({
                                      className: tickClass(model.bidTick),
                                      item: fmtNumber(quote.bid, {precision: 2})
                                  })
                              ]),
                              hbox([
                                  span({className: 'stock-chart-widget__quote-label', item: 'A:'}),
                                  span({
                                      className: tickClass(model.askTick),
                                      item: fmtNumber(quote.ask, {precision: 2})
                                  })
                              ])
                          ]
                      })
                    : null,

                quote?.isListed && quote.dayLow != null && quote.dayHigh != null
                    ? hbox({
                          className: 'stock-chart-widget__quote-range',
                          title: 'Day range',
                          items: [span({className: 'stock-chart-widget__quote-label', item: 'DAY'}), rangeBar(quote.dayLow, quote.dayHigh, price)]
                      })
                    : null,
                hspacer(25),
                toolbarButtonSeparator(),
                compactToolbarMenuButton({
                    icon: Icon.clock(),
                    tooltip: 'Date range',
                    menuItems: settingsModel.rangeTypes.map(opt => ({
                        text: opt.label,
                        active: formModel.values.range === opt.value,
                        actionFn: () => formModel.setValues({range: opt.value})
                    }))
                }),
                toolbarButtonSeparator(),
                compactToolbarMenuButton({
                    icon: Icon.chartLine(),
                    tooltip: 'Chart type',
                    menuItems: settingsModel.chartTypes.map(opt => ({
                        text: opt.label,
                        active: formModel.values.type === opt.value,
                        actionFn: () => formModel.setValues({type: opt.value})
                    }))
                }),
                toolbarButtonSeparator(),
                compactToolbarButton({
                    icon: chartSimpleIcon(),
                    active: formModel.values.showVolume,
                    onClick: () => {
                        formModel.setValues({showVolume: !formModel.values.showVolume});
                    },
                    tooltip: 'Show/Hide volume'
                }),
                toolbarButtonSeparator(),
                compactToolbarButton({
                    icon: Icon.calendar(),
                    active: !formModel.values.ordinal,
                    onClick: () => {
                        formModel.setValues({ordinal: !formModel.values.ordinal});
                    },
                    tooltip: 'Show/Hide weekends'
                }),
                toolbarButtonSeparator(),
                compactToolbarButton({
                    icon: sliderIcon(),
                    active: formModel.values.showNavigator,
                    onClick: () => {
                        formModel.setValues({showNavigator: !formModel.values.showNavigator});
                    },
                    tooltip: 'Show/Hide navigator'
                }),
                toolbarButtonSeparator(),
                indicatorPicker({
                    indicatorValue: formModel.values.indicator,
                    onValueChange: indicator => formModel.setValues({indicator})
                }),
                compactToolbarButton({
                    omit: !isActiveIndicator(formModel.values.indicator),
                    icon: Icon.x(),
                    tooltip: 'Remove indicator',
                    onClick: () => formModel.setValues({indicator: null})
                }),
                indicatorParamsPicker({
                    omit: !isActiveIndicator(formModel.values.indicator),
                    indicator: formModel.values.indicator,
                    params: formModel.values.indicatorParams?.[formModel.values.indicator],
                    onParamsChange: params =>
                        formModel.setValues({
                            indicatorParams: {
                                ...formModel.values.indicatorParams,
                                [formModel.values.indicator]: params
                            }
                        })
                }),
                toolbarButtonSeparator(),
                hspacer(25),
                stockToolsToolbar()
            ]
        });
    }
});
